/**
 * InlineChecklistPanel
 *
 * Renders booking checklists directly inside the expanded work-order card in My
 * Tasks.  Mechanics can tick checkbox items without opening the full
 * ChecklistFillDialog.  The dialog is still available via the "open" icon for
 * non-checkbox items or when a mechanic needs to mark a whole checklist
 * complete.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  useBookingChecklists,
  useUpdateChecklistItem,
  type BookingChecklist,
  type ChecklistTemplateItem,
  type BookingChecklistEntry,
} from '@/hooks/useChecklists';
import { ChecklistFillDialog } from '@/components/workorders/ChecklistFillDialog';
import { useNotify } from '@/hooks/useNotify';
import { queryKeys } from '@/hooks/queryKeys';

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function progressOf(cl: BookingChecklist): number {
  return cl.totalItems > 0
    ? Math.round((cl.completedCount / cl.totalItems) * 100)
    : 0;
}

function entryFor(
  cl: BookingChecklist,
  itemId: string,
): BookingChecklistEntry | undefined {
  return (cl.completedItems as BookingChecklistEntry[]).find(
    (e) => e.itemId === itemId,
  );
}

// ---------------------------------------------------------------------------
// sub-component: one checklist group
// ---------------------------------------------------------------------------

interface ChecklistGroupProps {
  checklist: BookingChecklist;
  onOpenDialog: (id: string) => void;
}

function ChecklistGroup({ checklist, onOpenDialog }: ChecklistGroupProps) {
  const { t } = useTranslation('checklists');
  const { t: tw } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const queryClient = useQueryClient();
  const updateItem = useUpdateChecklistItem();

  const isCompleted = checklist.status === 'completed';
  const progress = progressOf(checklist);

  // Show only checkbox items in inline mode; all other types need the dialog
  const checkboxItems = (checklist.items as ChecklistTemplateItem[]).filter(
    (item) => item.type === 'checkbox',
  );

  // Show at most the first few incomplete items plus already-completed ones
  const incompleteItems = checkboxItems.filter(
    (item) => !entryFor(checklist, item.id)?.checked,
  );
  const completedItems = checkboxItems.filter(
    (item) => entryFor(checklist, item.id)?.checked,
  );

  // Limit visible incomplete items to keep card compact
  const MAX_VISIBLE_INCOMPLETE = 5;
  const visibleIncomplete = incompleteItems.slice(0, MAX_VISIBLE_INCOMPLETE);
  const hiddenCount = incompleteItems.length - visibleIncomplete.length;

  const handleCheck = async (item: ChecklistTemplateItem, checked: boolean) => {
    try {
      await updateItem.mutateAsync({
        checklistId: checklist.id,
        itemId: item.id,
        checked,
      });
      // The mutation's optimistic update only patches the by-booking list when
      // the checklist detail is already in cache. Inline mode never loads
      // that detail, so invalidate the list this panel is drawn from (#2886).
      void queryClient.invalidateQueries({
        queryKey: queryKeys.checklists.byBooking(checklist.bookingId),
      });
    } catch (error) {
      notify.error(describeApiError(error, tc, { fallback: tw('myTasks.actionFailed') }));
    }
  };

  return (
    <Box
      sx={{
        mb: 1,
        p: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: isCompleted ? 'success.200' : 'divider',
        bgcolor: isCompleted ? 'success.50' : 'transparent',
      }}
    >
      {/* Header row */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <PlaylistAddCheckIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" fontWeight={600} noWrap>
            {checklist.name}
          </Typography>
          <Chip
            size="small"
            label={t(`status.${checklist.status}`)}
            color={isCompleted ? 'success' : 'default'}
            sx={{ height: 18, fontSize: '0.68rem' }}
          />
        </Stack>

        <Tooltip title={tw('myTasks.viewDetails')}>
          <IconButton
            size="small"
            aria-label={tw('myTasks.viewDetails')}
            onClick={(e) => {
              e.stopPropagation();
              onOpenDialog(checklist.id);
            }}
          >
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Progress bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          color={isCompleted ? 'success' : 'primary'}
          sx={{ flex: 1, height: 3, borderRadius: 1 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {checklist.completedCount}/{checklist.totalItems}
        </Typography>
      </Box>

      {/* Inline checkbox items */}
      {!isCompleted && checkboxItems.length > 0 && (
        <Box>
          {/* Completed items — read-only, visually subdued */}
          {completedItems.map((item) => (
            <Stack
              key={item.id}
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ ml: -0.5, opacity: 0.5 }}
            >
              <Checkbox
                size="small"
                checked
                disabled
                sx={{ p: 0.25 }}
              />
              <Typography
                variant="body2"
                sx={{ textDecoration: 'line-through', color: 'text.disabled' }}
              >
                {item.label}
              </Typography>
            </Stack>
          ))}

          {/* Next incomplete items */}
          {visibleIncomplete.map((item) => (
            <FormControlLabel
              key={item.id}
              onClick={(e) => e.stopPropagation()}
              disabled={updateItem.isPending}
              sx={{
                ml: -0.5,
                mr: 0,
                display: 'flex',
                minHeight: 44,
                alignItems: 'center',
              }}
              control={
                <Checkbox
                  size="small"
                  checked={false}
                  onChange={(e) => void handleCheck(item, e.target.checked)}
                  sx={{ p: 0.75 }}
                />
              }
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body2">{item.label}</Typography>
                  {item.required && (
                    <Chip
                      size="small"
                      label={t('booking.requiredItem')}
                      color="warning"
                      variant="outlined"
                      sx={{ height: 16, fontSize: '0.65rem' }}
                    />
                  )}
                </Stack>
              }
            />
          ))}

          {hiddenCount > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: 3, display: 'block', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                onOpenDialog(checklist.id);
              }}
            >
              {tw('myTasks.inlineChecklistMoreItems', { count: hiddenCount })}
            </Typography>
          )}
        </Box>
      )}

      {/* Non-checkbox items hint */}
      {!isCompleted &&
        checkboxItems.length === 0 &&
        checklist.totalItems > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              onOpenDialog(checklist.id);
            }}
          >
            {tw('myTasks.inlineChecklistOpenForItems')}
          </Typography>
        )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// main export
// ---------------------------------------------------------------------------

interface InlineChecklistPanelProps {
  /** The work order / booking ID used to fetch checklists. */
  workOrderId: string;
}

export function InlineChecklistPanel({ workOrderId }: InlineChecklistPanelProps) {
  const { t: tw } = useTranslation('workorders');
  const { data: checklists, isLoading } = useBookingChecklists(workOrderId);
  const [dialogChecklistId, setDialogChecklistId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">
          {tw('myTasks.loadingDetails')}
        </Typography>
      </Box>
    );
  }

  if (!checklists || checklists.length === 0) {
    return null;
  }

  return (
    <>
      <Box>
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ mb: 0.5, display: 'block', textTransform: 'uppercase' }}
        >
          {tw('myTasks.inlineChecklistsTitle')}
        </Typography>

        {checklists.map((cl: BookingChecklist) => (
          <ChecklistGroup
            key={cl.id}
            checklist={cl}
            onOpenDialog={setDialogChecklistId}
          />
        ))}
      </Box>

      <ChecklistFillDialog
        checklistId={dialogChecklistId}
        onClose={() => setDialogChecklistId(null)}
      />
    </>
  );
}
